import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DuplicateManager = ({ language, t }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);
  const [mergingGroup, setMergingGroup] = useState(null);
  const [mergeResults, setMergeResults] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all', 'high', 'medium', 'low'

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/exercises/duplicates/report?include_low_confidence=${includeLowConfidence}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch duplicate report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [includeLowConfidence]);

  const handleMerge = async (groupId, canonicalId = null) => {
    if (!window.confirm('Are you sure you want to merge this duplicate group? This action cannot be undone.')) {
      return;
    }

    try {
      setMergingGroup(groupId);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/exercises/duplicates/merge/${groupId}${canonicalId ? `?canonical_id=${canonicalId}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMergeResults(prev => [...prev, {
        groupId,
        success: true,
        message: `Merged ${response.data.deleted_count} duplicate(s) into "${response.data.canonical_name}"`
      }]);
      
      // Refresh the report
      await fetchReport();
    } catch (err) {
      setMergeResults(prev => [...prev, {
        groupId,
        success: false,
        message: err.response?.data?.detail || 'Merge failed'
      }]);
    } finally {
      setMergingGroup(null);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-green-500/20 text-green-200 border-green-400/40';
      case 'medium': return 'bg-yellow-500/20 text-yellow-200 border-yellow-400/40';
      case 'low': return 'bg-red-500/20 text-red-200 border-red-400/40';
      default: return 'bg-gray-500/20 text-gray-200';
    }
  };

  const getConfidenceIcon = (confidence) => {
    switch (confidence) {
      case 'high': return '🟢';
      case 'medium': return '🟡';
      case 'low': return '🔴';
      default: return '⚪';
    }
  };

  const filteredGroups = report?.duplicate_groups?.filter(group => {
    if (filter === 'all') return true;
    return group.confidence === filter;
  }) || [];

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="animate-spin text-4xl mb-4">🔄</div>
        <p className="text-blue-200">Analyzing exercises for duplicates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-red-400 text-4xl mb-4">⚠️</div>
        <p className="text-red-200">{error}</p>
        <button onClick={fetchReport} className="btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold text-white mb-4">🔍 Duplicate Exercise Detection</h2>
        
        {/* Summary Stats */}
        {report && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-white">{report.summary.total_exercises}</div>
              <div className="text-blue-300 text-sm">Total Exercises</div>
            </div>
            <div className="bg-purple-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-white">{report.summary.duplicate_groups_found}</div>
              <div className="text-purple-300 text-sm">Duplicate Groups</div>
            </div>
            <div className="bg-green-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-200">{report.summary.by_confidence.high}</div>
              <div className="text-green-300 text-sm">🟢 High Confidence</div>
            </div>
            <div className="bg-yellow-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-yellow-200">{report.summary.by_confidence.medium}</div>
              <div className="text-yellow-300 text-sm">🟡 Medium Confidence</div>
            </div>
            <div className="bg-red-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-red-200">{report.summary.by_confidence.low}</div>
              <div className="text-red-300 text-sm">🔴 Low Confidence</div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center space-x-2 text-blue-200">
            <input
              type="checkbox"
              checked={includeLowConfidence}
              onChange={(e) => setIncludeLowConfidence(e.target.checked)}
              className="rounded"
            />
            <span>Include LOW confidence matches</span>
          </label>
          
          <div className="flex items-center space-x-2">
            <span className="text-blue-200">Filter:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input-field py-1 px-2"
            >
              <option value="all">All Confidence Levels</option>
              <option value="high">🟢 High Only</option>
              <option value="medium">🟡 Medium Only</option>
              <option value="low">🔴 Low Only</option>
            </select>
          </div>

          <button onClick={fetchReport} className="btn-primary">
            🔄 Refresh Report
          </button>
        </div>
      </div>

      {/* Merge Results */}
      {mergeResults.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-lg font-semibold text-white mb-3">Recent Merge Operations</h3>
          <div className="space-y-2">
            {mergeResults.slice(-5).map((result, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-lg ${result.success ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}
              >
                {result.success ? '✅' : '❌'} {result.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duplicate Groups */}
      <div className="space-y-4">
        {filteredGroups.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <div className="text-green-400 text-4xl mb-4">✅</div>
            <p className="text-green-200">No duplicate groups found with current filters!</p>
          </div>
        ) : (
          filteredGroups.map((group) => (
            <div
              key={group.group_id}
              className={`glass-card border ${getConfidenceColor(group.confidence)}`}
            >
              {/* Group Header */}
              <div
                className="p-4 cursor-pointer flex items-center justify-between"
                onClick={() => toggleGroup(group.group_id)}
              >
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">{getConfidenceIcon(group.confidence)}</span>
                  <div>
                    <div className="text-white font-semibold">
                      {group.normalized_name}
                    </div>
                    <div className="text-sm text-blue-300">
                      {group.exercises.length} exercises • {group.confidence.toUpperCase()} confidence
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {group.confidence !== 'low' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMerge(group.group_id, group.proposed_canonical?.id);
                      }}
                      disabled={mergingGroup === group.group_id}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition disabled:opacity-50"
                    >
                      {mergingGroup === group.group_id ? '⏳ Merging...' : '🔗 Merge'}
                    </button>
                  )}
                  <span className="text-2xl">{expandedGroups.has(group.group_id) ? '▼' : '▶'}</span>
                </div>
              </div>

              {/* Group Details (Expanded) */}
              {expandedGroups.has(group.group_id) && (
                <div className="border-t border-blue-400/20 p-4 space-y-4">
                  {/* Reason */}
                  <div className="bg-blue-900/20 rounded-lg p-3">
                    <div className="text-blue-300 text-sm font-medium">Detection Reason:</div>
                    <div className="text-white">{group.reason}</div>
                  </div>

                  {/* Exercises in Group */}
                  <div>
                    <div className="text-blue-300 text-sm font-medium mb-2">Exercises in this group:</div>
                    <div className="space-y-2">
                      {group.exercises.map((ex) => (
                        <div
                          key={ex.id}
                          className={`p-3 rounded-lg ${ex.is_canonical ? 'bg-green-500/20 border border-green-400/40' : 'bg-gray-900/40'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-white font-medium">
                                {ex.is_canonical && <span className="text-green-400 mr-2">★</span>}
                                {ex.name}
                              </div>
                              <div className="text-sm text-blue-300 mt-1 space-x-3">
                                <span>🔧 {ex.equipment}</span>
                                <span>📂 {ex.category}</span>
                                <span>⭐ {ex.difficulty}</span>
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                Score: {ex.completeness_score} • Has Image: {ex.has_image ? '✅' : '❌'}
                              </div>
                            </div>
                            {!ex.is_canonical && group.confidence !== 'low' && (
                              <button
                                onClick={() => handleMerge(group.group_id, ex.id)}
                                disabled={mergingGroup === group.group_id}
                                className="px-3 py-1 bg-blue-500/30 hover:bg-blue-500/50 text-blue-200 rounded text-sm"
                              >
                                Set as Canonical
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Merge Preview */}
                  <div className="bg-purple-900/20 rounded-lg p-3">
                    <div className="text-purple-300 text-sm font-medium mb-2">After Merge:</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div className="text-white">
                        <span className="text-purple-300">Target Muscles:</span> {group.merge_preview.target_muscles}
                      </div>
                      <div className="text-white">
                        <span className="text-purple-300">Synergist Muscles:</span> {group.merge_preview.synergist_muscles}
                      </div>
                      <div className="text-white">
                        <span className="text-purple-300">Has Image:</span> {group.merge_preview.has_image ? '✅' : '❌'}
                      </div>
                      <div className="text-white">
                        <span className="text-purple-300">Instructions:</span> {group.merge_preview.instructions_length} chars
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Info Box */}
      <div className="glass-card p-4 bg-blue-900/20 border border-blue-400/30">
        <h3 className="text-lg font-semibold text-blue-200 mb-2">ℹ️ About Confidence Levels</h3>
        <ul className="space-y-2 text-sm text-blue-300">
          <li><span className="text-green-400">🟢 HIGH:</span> Exact name + equipment + category match. Safe to merge.</li>
          <li><span className="text-yellow-400">🟡 MEDIUM:</span> Name + equipment match, but different attributes. Review before merging.</li>
          <li><span className="text-red-400">🔴 LOW:</span> Similar names but different equipment or category. Manual review only - not auto-mergeable.</li>
        </ul>
      </div>
    </div>
  );
};

export default DuplicateManager;
